import { useState, useEffect, useCallback } from 'react';

export interface SearchTermProps {
  searchText: string;
}

/**
 * Custom hook that:
 * 1) Initializes an array of `count` unique random items from `searchTerms`.
 * 2) Every `intervalMs` milliseconds, selects a random term **that is not currently** in `displayTerms` and:
 *    - Removes the last item in `displayTerms` (if we have reached `count`)
 *    - Unshifts the newly picked term to the front
 *
 * If no unused terms are available (i.e., every term is already in `displayTerms`),
 * we skip updating for that interval.
 *
 * Items that have been popped off can reappear in the future (no global discard).
 * We never move an existing item to the front – if the random pick is in `displayTerms`,
 * we discard it and pick another one instead.
 *
 * @param {SearchTermProps[]} searchTerms - Full list of search term objects.
 * @param {number} [intervalMs=2000] - Interval in milliseconds (2s by default).
 * @param {number} [count=8] - How many items to display.
 *
 * @returns {object} - An object containing:
 *   - `displayTerms`: The current list of terms (max length = `count`)
 *   - `pauseTrendingFeed`: Function to pause updates
 *   - `resumeTrendingFeed`: Function to resume updates
 */
export function useRandomSearchTerms(searchTerms: SearchTermProps[], intervalMs = 4000, count = 5) {
  // Step 1: Initialize `displayTerms` with `count` unique random items
  const [displayTerms, setDisplayTerms] = useState<SearchTermProps[]>(() => {
    // Safely handle the case where `searchTerms` has fewer than `count` items
    const uniqueCount = Math.min(count, searchTerms.length);

    // A simple utility to get random unique items
    const shuffled = [...searchTerms].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, uniqueCount);
  });

  // Control whether the feed is paused
  const [isPaused, setIsPaused] = useState(false);

  // Step 2: Periodically pick a random new item (that's NOT in `displayTerms`)
  useEffect(() => {
    if (isPaused) return;

    const intervalId = setInterval(() => {
      setDisplayTerms(prevTerms => {
        // Available terms = all items NOT in `prevTerms`
        const usedSearchTexts = new Set(prevTerms.map(t => t.searchText));
        const available = searchTerms.filter(term => !usedSearchTexts.has(term.searchText));

        // If there's nothing available, do nothing this round
        if (available.length === 0) {
          return prevTerms;
        }

        // Pick a random item from 'available'
        const randomIndex = Math.floor(Math.random() * available.length);
        const randomTerm = available[randomIndex];

        // Create a copy for safe mutation
        const newList = [...prevTerms];

        // If we already have `count` items, remove the last one
        if (newList.length >= count) {
          newList.pop();
        }

        // Add the new item to the front
        newList.unshift(randomTerm);

        return newList;
      });
    }, intervalMs);

    // Cleanup on unmount
    return () => clearInterval(intervalId);
  }, [searchTerms, intervalMs, isPaused, count]);

  // Public functions to pause/resume
  const pauseTrendingFeed = useCallback(() => setIsPaused(true), []);
  const resumeTrendingFeed = useCallback(() => setIsPaused(false), []);

  return { displayTerms, pauseTrendingFeed, resumeTrendingFeed };
}
